import { useMemo, useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import {
  Phone,
  MessageCircle,
  Quote,
  Anchor,
  HandHeart,
  Users,
  BriefcaseBusiness,
  Star,
} from "lucide-react";
import { HOTEL, OWNER } from "../data/site";

const ICONS = { Anchor, HandHeart, Users, BriefcaseBusiness };

function FloatingDust() {
  const dots = useMemo(
    () =>
      Array.from({ length: 24 }).map((_, i) => ({
        id: i,
        left: Math.random() * 100,
        top: Math.random() * 100,
        size: 1 + Math.random() * 3,
        delay: -Math.random() * 9,
        duration: 7 + Math.random() * 9,
        opacity: 0.35 + Math.random() * 0.5,
      })),
    [],
  );
  return (
    <div aria-hidden className="absolute inset-0 pointer-events-none overflow-hidden">
      <style>{`
        @keyframes mdp-owner-drift {
          0%, 100% { transform: translate3d(0,0,0); opacity: 0.2; }
          50%      { transform: translate3d(8px,-22px,0); opacity: 1; }
        }
      `}</style>
      {dots.map((d) => (
        <span
          key={d.id}
          className="absolute rounded-full"
          style={{
            left: `${d.left}%`,
            top: `${d.top}%`,
            width: `${d.size}px`,
            height: `${d.size}px`,
            backgroundColor: "#E6C555",
            boxShadow: "0 0 8px rgba(212,175,55,0.6)",
            opacity: d.opacity,
            animation: `mdp-owner-drift ${d.duration}s ease-in-out ${d.delay}s infinite`,
          }}
        />
      ))}
    </div>
  );
}

export default function Owner() {
  const portraits = OWNER.portraits && OWNER.portraits.length > 0 ? OWNER.portraits : [OWNER.portrait];
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (portraits.length < 2 || paused) return;
    timerRef.current = setInterval(() => {
      setActive((i) => (i + 1) % portraits.length);
    }, 5000);
    return () => clearInterval(timerRef.current);
  }, [portraits.length, paused]);

  return (
    <section
      id="owner"
      data-testid="owner-section"
      className="relative section-padding overflow-hidden bg-[#140A0A] text-white"
    >
      {/* Layered cinematic background */}
      <div aria-hidden className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(212,175,55,0.18),transparent_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(122,26,36,0.45),transparent_60%)]" />
      </div>
      <div aria-hidden className="absolute inset-0 grain pointer-events-none opacity-100" />
      <FloatingDust />

      <div className="container-x relative">
        <div className="max-w-2xl">
          <span className="text-xs sm:text-sm font-medium tracking-[0.25em] uppercase text-[#E6C555]">
            Meet Our Owner
          </span>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl leading-tight">
            Meet Our Founder —<br />
            <span className="italic text-[#D4AF37]">{OWNER.name}.</span>
          </h2>
          <p className="mt-4 text-sm sm:text-base tracking-wide text-white/70">
            {OWNER.title}
          </p>
        </div>

        <div className="mt-14 grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          {/* Portrait column */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5 relative"
          >
            <div className="relative">
              {/* Decorative gold ring */}
              <div
                aria-hidden
                className="absolute -inset-3 sm:-inset-4 rounded-3xl border border-[#D4AF37]/35 [box-shadow:0_0_60px_-10px_rgba(212,175,55,0.4)_inset]"
              />
              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
                onMouseEnter={() => setPaused(true)}
                onMouseLeave={() => setPaused(false)}
                className="relative aspect-[4/5] overflow-hidden rounded-3xl ring-1 ring-[#D4AF37]/40 shadow-2xl shadow-black/50 bg-[#1A1010]"
              >
                {/* Cross-fading founder portraits with subtle ken-burns zoom */}
                <style>{`
                  @keyframes mdp-kenburns {
                    0%   { transform: scale(1.02); }
                    100% { transform: scale(1.10); }
                  }
                `}</style>
                {portraits.map((src, idx) => (
                  <img
                    key={src}
                    src={src}
                    alt={`${OWNER.name} — ${OWNER.title}`}
                    loading={idx === 0 ? "eager" : "lazy"}
                    decoding="async"
                    data-testid={idx === 0 ? "owner-portrait" : `owner-portrait-${idx}`}
                    className="absolute inset-0 h-full w-full object-cover object-center transition-opacity duration-[1600ms] ease-[cubic-bezier(0.4,0,0.2,1)] will-change-[opacity,transform]"
                    style={{
                      opacity: idx === active ? 1 : 0,
                      animation: idx === active ? "mdp-kenburns 9s ease-in-out forwards" : "none",
                    }}
                  />
                ))}
                {/* Cinematic warm light wash */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A0608]/85 via-[#1A0608]/10 to-[#D4AF37]/10" />
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(212,175,55,0.18),transparent_60%)]" />

                {/* Plate */}
                <div className="absolute bottom-4 left-4 right-4 rounded-2xl px-5 py-4 backdrop-blur-md bg-black/40 border border-white/10">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#A8862B] grid place-items-center text-[#1A1515] font-display font-semibold">
                      {OWNER.initials}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display text-lg leading-tight truncate">
                        {OWNER.name}
                      </div>
                      <div className="text-[10px] tracking-[0.2em] uppercase text-[#E6C555]/90 mt-0.5 truncate">
                        Founder · Maa Durga Palace
                      </div>
                    </div>
                    <div className="flex items-center gap-0.5 shrink-0">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <Star key={s} size={11} className="fill-[#D4AF37] text-[#D4AF37]" />
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
              {/* Floating glow accent */}
              <div
                aria-hidden
                className="absolute -bottom-6 -left-6 h-20 w-20 rounded-full bg-[#D4AF37]/30 blur-3xl"
              />
            </div>
          </motion.div>

          {/* Text + badges + CTAs */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="lg:col-span-7"
          >
            <div className="space-y-5 text-white/85 leading-relaxed">
              {OWNER.paragraphs.map((p, i) => (
                <motion.p
                  key={p.slice(0, 32)}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, duration: 0.55 }}
                  className="text-base sm:text-lg"
                >
                  {p}
                </motion.p>
              ))}
            </div>

            {/* Quote */}
            <motion.figure
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.25, duration: 0.7 }}
              data-testid="owner-quote"
              className="mt-9 glass-card rounded-2xl p-6 sm:p-7 border-l-4 border-l-[#D4AF37]"
            >
              <Quote size={22} className="text-[#D4AF37]" />
              <blockquote className="mt-3 font-display italic text-xl sm:text-2xl leading-snug text-white">
                “{OWNER.quote}”
              </blockquote>
              <figcaption className="mt-3 text-xs tracking-[0.25em] uppercase text-[#E6C555]">
                — {OWNER.name}
              </figcaption>
            </motion.figure>

            {/* Badges */}
            <div
              data-testid="owner-badges"
              className="mt-9 grid grid-cols-2 sm:grid-cols-4 gap-3"
            >
              {OWNER.badges.map((b, i) => {
                const Icon = ICONS[b.icon] || Anchor;
                return (
                  <motion.div
                    key={b.label}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 + i * 0.08, duration: 0.5 }}
                    whileHover={{ y: -4 }}
                    data-testid={`owner-badge-${i}`}
                    className="group relative rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-sm p-4 overflow-hidden hover:border-[#D4AF37]/50 transition-colors"
                  >
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-br from-[#D4AF37]/15 to-transparent transition-opacity" />
                    <div className="relative">
                      <div className="h-9 w-9 rounded-lg bg-[#D4AF37]/15 text-[#E6C555] grid place-items-center">
                        <Icon size={17} strokeWidth={1.7} />
                      </div>
                      <div className="mt-3 font-display text-sm sm:text-[15px] leading-snug">
                        {b.label}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.55, duration: 0.6 }}
              className="mt-10 flex flex-wrap items-center gap-3"
            >
              <a
                href={`tel:${HOTEL.phoneE164}`}
                data-testid="owner-call-btn"
                className="btn-gold"
              >
                <Phone size={16} />
                Call Owner
              </a>
              <a
                href={HOTEL.whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="owner-whatsapp-btn"
                className="btn-whatsapp"
              >
                <MessageCircle size={16} />
                WhatsApp Inquiry
              </a>
              <span className="text-xs text-white/55 ml-1">
                {HOTEL.phoneFormatted} · {HOTEL.address.city}, {HOTEL.address.state}
              </span>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
